import React, { useState } from 'react';
import { X, Plus, Check, LogOut, Settings, Shield, User, Crown } from 'lucide-react';

interface Account {
  id: string;
  username: string;
  displayName: string;
  avatar: string;
  isActive: boolean;
  isVerified?: boolean;
  isPremium?: boolean;
  followerCount?: number;
}

interface SwitchAccountsDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SwitchAccountsDialog({ isOpen, onClose }: SwitchAccountsDialogProps) {
  const [accounts, setAccounts] = useState<Account[]>([
    {
      id: '1',
      username: 'chareen3',
      displayName: 'Chareen',
      avatar: 'https://cdn.pixabay.com/photo/2024/02/28/07/42/european-shorthair-8601492_1280.jpg',
      isActive: true,
      isVerified: true,
      isPremium: true,
      followerCount: 12500
    },
    {
      id: '2',
      username: 'photography_pro',
      displayName: 'Pro Photographer',
      avatar: 'https://cdn.pixabay.com/photo/2024/01/15/20/12/ai-generated-8509896_1280.jpg',
      isActive: false,
      isVerified: true,
      followerCount: 8200
    },
    {
      id: '3',
      username: 'nature_lover',
      displayName: 'Nature Explorer',
      avatar: 'https://cdn.pixabay.com/photo/2024/01/19/16/38/woman-8520135_1280.jpg',
      isActive: false,
      followerCount: 3400
    }
  ]);

  const handleSwitchAccount = (accountId: string) => {
    setAccounts(accounts.map(account => ({
      ...account,
      isActive: account.id === accountId
    })));
    
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleLogout = (accountId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setAccounts(accounts.filter(account => account.id !== accountId));
  };

  const formatFollowerCount = (count: number) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-md mx-4 max-h-[80vh] overflow-hidden animate-in fade-in-0 zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Switch Accounts</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Choose an account to continue</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="max-h-[50vh] overflow-y-auto scrollbar-hide">
          {/* Current Account */}
          <div className="p-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
              <User className="w-4 h-4 mr-2" />
              Current Account
            </h3>
            {accounts.filter(account => account.isActive).map(account => (
              <div key={account.id} className="bg-green-50 dark:bg-green-900/20 border-2 border-[#065900] rounded-xl p-4">
                <div className="flex items-center">
                  <div className="relative">
                    <img
                      src={account.avatar}
                      alt={account.displayName}
                      className="w-12 h-12 rounded-full object-cover ring-2 ring-[#065900]"
                    />
                    <div className="absolute -bottom-1 -right-1 bg-[#065900] text-white rounded-full p-1">
                      <Check className="w-3 h-3" />
                    </div>
                  </div>
                  <div className="ml-4 flex-1">
                    <div className="flex items-center">
                      <h4 className="font-semibold text-gray-900 dark:text-white">{account.displayName}</h4>
                      {account.isVerified && (
                        <Shield className="w-4 h-4 ml-1 text-blue-500" />
                      )}
                      {account.isPremium && (
                        <Crown className="w-4 h-4 ml-1 text-yellow-500" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">@{account.username}</p>
                    {account.followerCount && (
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        {formatFollowerCount(account.followerCount)} followers
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Other Accounts */}
          {accounts.filter(account => !account.isActive).length > 0 && (
            <div className="px-4 pb-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                <Settings className="w-4 h-4 mr-2" />
                Switch To
              </h3>
              <div className="space-y-2">
                {accounts.filter(account => !account.isActive).map(account => (
                  <div
                    key={account.id}
                    onClick={() => handleSwitchAccount(account.id)}
                    className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-all duration-200 group border border-transparent hover:border-gray-200 dark:hover:border-gray-700"
                  >
                    <div className="flex items-center flex-1">
                      <div className="relative">
                        <img
                          src={account.avatar}
                          alt={account.displayName}
                          className="w-10 h-10 rounded-full object-cover ring-1 ring-gray-200 dark:ring-gray-700 group-hover:ring-[#065900]"
                        />
                      </div>
                      <div className="ml-3 flex-1">
                        <div className="flex items-center">
                          <h4 className="font-medium text-gray-900 dark:text-white text-sm group-hover:text-[#065900]">
                            {account.displayName}
                          </h4>
                          {account.isVerified && (
                            <Shield className="w-3 h-3 ml-1 text-blue-500" />
                          )}
                          {account.isPremium && (
                            <Crown className="w-3 h-3 ml-1 text-yellow-500" />
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">@{account.username}</p>
                        {account.followerCount && (
                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            {formatFollowerCount(account.followerCount)} followers
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={(e) => handleLogout(account.id, e)}
                      className="p-2 rounded-full hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                      title="Remove account"
                    >
                      <LogOut className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800/50">
          <button
            onClick={() => {
              // Handle add account functionality
              console.log('Add new account');
              onClose();
            }}
            className="w-full flex items-center justify-center px-4 py-3 bg-[#065900] hover:bg-[#054700] text-white rounded-xl font-medium transition-colors duration-200 shadow-sm hover:shadow-md"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Another Account
          </button>
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-3">
            Manage your accounts securely with MoZo
          </p>
        </div>
      </div>
    </div>
  );
}
